import {
  Drawer,
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Input,
  Divider,
  Empty,
  Spin,
  Modal,
  message,
} from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import dayjs from "dayjs";
import FinanceService from "../../../dependencies/custom/financeService";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { useNavigate } from "react-router-dom";
import StudentMultiSelect from "../../common/StudentMultiSelect";

const CustomFeeDetailDrawer = ({
  visible,
  onClose,
  groupData,
  students = [],
  onApprove,
  onReject,
  onApproveStudent,
  onRejectStudent,
  onRemoveStudent,
  onAddStudents,
  loading = false,
  groupKey,
  allStudentsPending = false,
}) => {
  const navigate = useNavigate();
  const [studentSearch, setStudentSearch] = useState("");
  const [addStudentModalVisible, setAddStudentModalVisible] = useState(false);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [selectedNewStudents, setSelectedNewStudents] = useState([]);

  if (!groupData) return null;

  const filteredStudents = students.filter((student) => {
    if (!studentSearch) return true;
    const search = studentSearch.toLowerCase();
    const name = (student.studentName || "").toLowerCase();
    const spc = (student.studentSpc || student.spc || "").toLowerCase();
    return name.includes(search) || spc.includes(search);
  });

  const fetchAvailableStudents = async () => {
    setLoadingAvailable(true);
    try {
      const params = [];
      params.push(`acadyr=${encodeURIComponent(groupData.acadyr)}`);
      params.push("status=ADMITTED");

      const queryString = params.join("&");
      const url = `${domainSettings.urls.enrollment.student}?${queryString}`;

      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );

      if (response.ok) {
        const studentsData = response.data?.results || response.data || [];
        const validStudentsData = Array.isArray(studentsData)
          ? studentsData
          : [];
        const groupStudentSpcs = students.map((s) => s.studentSpc);
        const filtered = validStudentsData.filter(
          (s) => !groupStudentSpcs.includes(s.spcCode || s.spc),
        );
        setAvailableStudents(filtered);
      } else {
        message.error("Failed to fetch available students");
      }
    } catch (error) {
      console.error("Error fetching available students:", error);
      message.error("An error occurred while fetching students");
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleAddStudentsClick = () => {
    setSelectedNewStudents([]);
    fetchAvailableStudents();
    setAddStudentModalVisible(true);
  };

  const handleAddStudentsConfirm = async () => {
    if (selectedNewStudents.length === 0) {
      message.error("Please select at least one student");
      return;
    }

    await onAddStudents(groupData, selectedNewStudents);
    setAddStudentModalVisible(false);
    setSelectedNewStudents([]);
  };

  const studentColumns = [
    {
      title: "Request ID",
      key: "customFeeRequestId",
      dataIndex: "customFeeRequestId",
      width: 80,
      render: (text) => text || "-",
    },
    {
      title: "Student Name",
      key: "studentName",
      dataIndex: "studentName",
      render: (text) => text || "-",
    },
    {
      title: "SPC",
      key: "studentSpc",
      dataIndex: "studentSpc",
      render: (text) => text || "-",
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      width: 100,
      align: "center",
      render: (status) => {
        if (status === "Pending") return <Tag color="gold">{status}</Tag>;
        if (status === "Approved") return <Tag color="green">{status}</Tag>;
        if (status === "Rejected") return <Tag color="red">{status}</Tag>;
        return <Tag>{status}</Tag>;
      },
    },
    {
      title: "Rejection Reason",
      key: "rejectionReason",
      dataIndex: "rejectionReason",
      width: 180,
      render: (reason) => {
        if (!reason) return "-";
        return (
          <span style={{ color: "#cf1322", fontSize: "13px" }}>{reason}</span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      align: "center",
      render: (_, record) => {
        const isStudentPending = record.status === "Pending";
        return (
          <Space size="small">
            {isStudentPending && (
              <>
                <Button
                  type="link"
                  size="small"
                  style={{ color: "#52c41a" }}
                  title="Approve this student"
                  icon={<CheckOutlined />}
                  onClick={() =>
                    onApproveStudent &&
                    onApproveStudent(
                      record.customFeeRequestId,
                      record.studentSpc,
                    )
                  }
                />
                <Button
                  type="link"
                  danger
                  size="small"
                  title="Reject this student"
                  icon={<CloseOutlined />}
                  onClick={() =>
                    onRejectStudent &&
                    onRejectStudent(
                      record.customFeeRequestId,
                      record.studentSpc,
                    )
                  }
                />
                <Button
                  type="link"
                  danger
                  size="small"
                  title="Remove from group"
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    onRemoveStudent &&
                    onRemoveStudent(
                      record.customFeeRequestId,
                      record.studentSpc,
                    )
                  }
                />
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Drawer
      title={`Custom Fee Request Group - ${students.length} Student(s)`}
      placement="right"
      onClose={onClose}
      open={visible}
      width={800}
      bodyStyle={{ paddingBottom: 80 }}
    >
      <Spin spinning={loading}>
        <Descriptions
          title="Request Information"
          bordered
          column={1}
          size="small"
          style={{ marginBottom: 24 }}
        >
          <Descriptions.Item label="Fee Type">
            <Tag color="blue">{groupData.feeTypeName}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Description">
            {groupData.description || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Amount">
            {FinanceService.formatCurrency(groupData.amount)}
          </Descriptions.Item>
          <Descriptions.Item label="Academic Year">
            {groupData.acadyr}
          </Descriptions.Item>
          <Descriptions.Item label="Created Date">
            {dayjs(groupData.createdAt).format("YYYY-MM-DD HH:mm")}
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <h4>Students ({students.length})</h4>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAddStudentsClick}
            >
              Add Students
            </Button>
          </div>

          <Input
            placeholder="Search by name or SPC..."
            prefix={<SearchOutlined />}
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
            allowClear
            style={{ marginBottom: 16 }}
          />
          {students.length > 0 ? (
            <Table
              columns={studentColumns}
              dataSource={filteredStudents}
              rowKey={(record) => record.studentSpc}
              pagination={{ pageSize: 10 }}
              size="small"
            />
          ) : (
            <Empty description="No students in this group" />
          )}
        </div>

        {allStudentsPending && (
          <>
            <Divider />
            <Space wrap>
              <Button
                type="primary"
                onClick={() => onApprove && onApprove(groupData.groupKey)}
              >
                Approve All
              </Button>
              <Button
                danger
                onClick={() => onReject && onReject(groupData.groupKey)}
              >
                Reject All
              </Button>
            </Space>
          </>
        )}
      </Spin>

      <Modal
        title="Add Students to Group"
        open={addStudentModalVisible}
        onOk={handleAddStudentsConfirm}
        onCancel={() => {
          setAddStudentModalVisible(false);
          setSelectedNewStudents([]);
        }}
        width={600}
        okText="Add"
        confirmLoading={loadingAvailable}
      >
        <p>
          Select students from <strong>{groupData.acadyr}</strong>:
        </p>
        <StudentMultiSelect
          students={availableStudents}
          value={selectedNewStudents}
          onChange={setSelectedNewStudents}
          placeholder={`Found ${availableStudents.length} available student(s)`}
          loading={loadingAvailable}
          style={{ width: "100%" }}
        />
      </Modal>
    </Drawer>
  );
};

export default CustomFeeDetailDrawer;
